// ============================================================================
// LE NUMÉRO D'UN DOCUMENT D'ACHAT — proposé par l'écran, tapé par l'opérateur, jugé par le serveur.
//
// UTILITÉ : l'éditeur de commandes et de bons PROPOSE un numéro (premier mot du fournisseur + date du
// jour, `TEXTIL-140826`), l'opérateur peut le changer, et le serveur refuse ce qui ne peut pas être une
// clé. La proposition et la forme vivent ICI parce que trois lecteurs les appliquent : l'éditeur du
// back-office, la numérotation automatique des bons au serveur, et le BL saisi dans le même éditeur.
// Écrites deux fois, elles divergeraient — et l'écran proposerait un numéro que le serveur refuse.
//
// ⚠️ UN NUMÉRO EST UNE CLÉ, PAS UN TEXTE : il entre dans l'identifiant du document (`documentId`) et le
//    fournisseur le rappelle sur son BL et sa facture. D'où un alphabet FERMÉ — lettres, chiffres,
//    « - _ / . » — et une longueur bornée : un espace ou un accent ne se relit pas au téléphone.
// ============================================================================

/** La longueur maximale d'un numéro tapé — celle que l'écran laisse saisir et que le serveur accepte. */
export const DOCUMENT_NUMBER_MAX = 30;

/** Le premier mot d'un fournisseur ne prend pas plus de place : le jour et un suffixe doivent tenir derrière. */
const BASE_MAX = 20;

const ALLOWED = /^[A-Z0-9][A-Z0-9\-_/.]*$/;

/** Retire les accents : « É » devient « E », jamais rien — un mot amputé de ses lettres ne se reconnaît plus. */
const foldAccents = (v: string): string => v.normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Ce que le champ garde de ce que l'opérateur tape : majuscules, accents repliés, et rien hors de l'alphabet
 * du numéro.
 * ⚠️ LE CHAMP NETTOIE À LA FRAPPE, LE SERVEUR REFUSE : un numéro qui arriverait autrement (API, ancien
 *    écran) n'est jamais réparé en silence, il est refusé en le disant.
 */
export function typedDocumentNumber(raw: string): string {
  return foldAccents(raw).toUpperCase().replace(/[^A-Z0-9\-_/.]/g, '').slice(0, DOCUMENT_NUMBER_MAX);
}

/** Pourquoi un numéro (déjà en majuscules) ne peut pas être une clé — `null` s'il le peut. */
export type DocumentNumberRefusal = 'empty' | 'too_long' | 'bad_chars';

export function documentNumberRefusal(number: string): DocumentNumberRefusal | null {
  if (number === '') return 'empty';
  if (number.length > DOCUMENT_NUMBER_MAX) return 'too_long';
  return ALLOWED.test(number) ? null : 'bad_chars';
}

/**
 * Le premier mot d'un fournisseur qui n'a ni lettre ni chiffre est remplacé par le type du document.
 * ⚠️ CE SONT DES MORCEAUX DE CLÉ, PAS DES MOTS : ils entrent dans un numéro que le fournisseur recopie, ils ne
 *    se traduisent jamais — c'est pourquoi ils vivent ici et non dans un écran.
 */
export const DOCUMENT_NUMBER_FALLBACK = { order: 'CMD', deliveryNote: 'BL' } as const;

/** Un jour civil — jamais un instant : le numéro dit le jour du magasin, pas l'heure du serveur. */
export interface CivilDay { year: number; month: number; day: number }

/**
 * LE NUMÉRO PROPOSÉ — premier mot du fournisseur, sans accent, en majuscules, suivi du jour `JJMMAA` ;
 * `-2`, `-3`… si le numéro est déjà pris (`TEXTIL-140826`, `TEXTIL-140826-2`).
 * ⚠️ `isTaken` couvre DEUX ensembles : les documents déjà en base ET les numéros déjà retenus par les
 *    autres fournisseurs de la même saisie — sinon deux commandes du même lot recevraient le même.
 * ⚠️ Le jour est FOURNI par l'appelant : l'écran prend le jour du navigateur, le serveur le sien ; un
 *    numéro qui lirait l'horloge ici ne serait pas reproductible en test.
 */
export function proposeDocumentNumber(supplier: string, day: CivilDay, isTaken: (n: string) => boolean, fallback: string): string {
  const base = (foldAccents(supplier).toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean)[0] || fallback).slice(0, BASE_MAX);
  const p2 = (n: number) => String(n).padStart(2, '0');
  const root = `${base}-${p2(day.day)}${p2(day.month)}${String(day.year).slice(-2)}`;
  let n = 2;
  let number = root;
  while (isTaken(number)) number = `${root}-${n++}`;
  return number;
}
